'use client';

import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

interface CollapsibleSectionProps {
  title: string;
  accentColor?: 'orange' | 'navy';
  defaultOpen?: boolean;
  children: React.ReactNode;
  className?: string;
}

export default function CollapsibleSection({
  title,
  accentColor = 'orange',
  defaultOpen = true,
  children,
  className = '',
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  const accentBarColor = accentColor === 'orange' ? '#F97316' : '#2D5A8E';
  const accentTextColor = accentColor === 'orange' ? '#F97316' : '#4A7FB5';

  return (
    <div className={`mb-4 sm:mb-5 ${className}`}>
      {/* Section header */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 mb-3 group focus:outline-none min-h-[44px] py-1 touch-manipulation rounded-lg px-1 -mx-1 transition-colors duration-150 hover:bg-white/[0.02]"
        aria-expanded={open}
        aria-label={`${open ? 'Collapse' : 'Expand'} ${title} section`}
      >
        <div className="flex items-center gap-2.5">
          <div
            className="w-[3px] h-[18px] rounded-full flex-shrink-0 transition-opacity duration-200 group-hover:opacity-100"
            style={{ background: accentBarColor, opacity: open ? 1 : 0.5 }}
          />
          <span
            className="text-[11px] md:text-[12px] font-bold uppercase tracking-[0.16em] md:tracking-[0.18em] text-left transition-colors duration-150"
            style={{ color: open ? accentTextColor : '#52525b' }}
          >
            {/* Strip leading emoji from title for cleaner look */}
            {title.replace(/^[\p{Emoji}\s]+/u, '').trim() || title}
          </span>
        </div>
        {/* Chevron */}
        <ChevronDown
          size={14}
          className="transition-transform duration-300 flex-shrink-0 opacity-40 group-hover:opacity-70"
          style={{ color: accentTextColor, transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>

      {/* Collapsible content */}
      <div
        className={`transition-all duration-300 overflow-hidden ${
          open ? 'max-h-[9999px] opacity-100' : 'max-h-0 opacity-0 pointer-events-none'
        }`}
      >
        {children}
      </div>
    </div>
  );
}
